import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getMyOrganizations,
  getPostableOrganizations,
  type UserOrganization,
} from "@tickethall/core";
import { safeStorage } from "@/lib/storage";
import { useAuth } from "@/contexts/AuthContext";

interface ActiveContext {
  type: "personal" | "organization";
  organizationId: string | null;
  organizationName: string | null;
  organizationSlug: string | null;
  organizationLogo: string | null;
}

interface OrganizationContextType {
  activeContext: ActiveContext;
  setActiveContext: (ctx: ActiveContext) => void;
  organizations: UserOrganization[];
  postableOrganizations: UserOrganization[];
  isLoading: boolean;
  refetch: () => void;
}

const STORAGE_KEY = "th_active_posting_context";

function getStoredContext(): ActiveContext {
  const raw = safeStorage.get(STORAGE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      // JSON parse error, return default
    }
  }
  return {
    type: "personal",
    organizationId: null,
    organizationName: null,
    organizationSlug: null,
    organizationLogo: null,
  };
}

const OrganizationContext = createContext<OrganizationContextType>({
  activeContext: getStoredContext(),
  setActiveContext: () => {},
  organizations: [],
  postableOrganizations: [],
  isLoading: false,
  refetch: () => {},
});

export const useOrganization = () => useContext(OrganizationContext);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [activeContext, setActiveContextState] = useState<ActiveContext>(getStoredContext);
  const { user } = useAuth();

  const {
    data: organizations = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["my-organizations", user?.id],
    queryFn: getMyOrganizations,
    enabled: !!user?.id,
  });

  const { data: postableOrganizations = [] } = useQuery({
    queryKey: ["postable-organizations", user?.id],
    queryFn: getPostableOrganizations,
    enabled: !!user?.id,
  });

  const setActiveContext = (ctx: ActiveContext) => {
    setActiveContextState(ctx);
    safeStorage.set(STORAGE_KEY, JSON.stringify(ctx));
  };

  useEffect(() => {
    if (
      activeContext.type === "organization" &&
      activeContext.organizationId &&
      organizations.length > 0
    ) {
      const stillMember = organizations.find(
        (o) =>
          o.org_id === activeContext.organizationId &&
          o.member_status === "active"
      );
      if (!stillMember) {
        setActiveContext({
          type: "personal",
          organizationId: null,
          organizationName: null,
          organizationSlug: null,
          organizationLogo: null,
        });
      }
    }
  }, [organizations, activeContext.organizationId, activeContext.type]);

  return (
    <OrganizationContext.Provider
      value={{
        activeContext,
        setActiveContext,
        organizations,
        postableOrganizations,
        isLoading,
        refetch,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

